import type { ReactNode } from 'react';
import { Suspense, useMemo, useState } from 'react';

import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { lazy } from '@sorare/core/src/lib/retry';

import { BuyConfirmationProvider as BuyingConfirmationProvider } from 'contexts/buyingConfirmation/Provider';

import type { MarketplaceContextType } from '.';
import { MarketplaceProvider as MarketplaceContextProvider } from '.';

const AddToCartPopin = lazy(
  async () => import('components/cart/AddToCartPopin'),
  'AddToCartPopin'
);

interface Props
  extends Omit<
    MarketplaceContextType,
    'hideDetails' | 'setHideDetails' | 'showAddToCartPopin'
  > {
  children: ReactNode;
}

export const MarketplaceProvider = ({ children }: Props) => {
  const [hideDetails, setHideDetails] = useState(false);
  const [addToCartSlugs, setAddToCartSlugs] = useState<string[] | null>(null);

  const value = useMemo(
    () => ({
      hideDetails,
      setHideDetails,
      showAddToCartPopin: (s: string[] | null) => setAddToCartSlugs(s),
    }),
    [hideDetails]
  );

  return (
    <MarketplaceContextProvider value={value}>
      {!!addToCartSlugs?.length && (
        <Suspense fallback={<LoadingIndicator />}>
          <AddToCartPopin
            slugs={addToCartSlugs}
            onClose={() => {
              setAddToCartSlugs(null);
            }}
          />
        </Suspense>
      )}
      <BuyingConfirmationProvider>{children}</BuyingConfirmationProvider>
    </MarketplaceContextProvider>
  );
};
